import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import { apiFetch } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import { isSoundMuted, setSoundMuted, primeAudio, playAlert } from '../lib/notificationSound';

// ---------------------------------------------------------------------------
// NotificationBell
// ---------------------------------------------------------------------------
// The in-tenant notification bus (migration 0021) surfaced as a bell in the
// TopNavBar. useNotifications() polls /notifications/count every 30s for the
// badge and fires a desktop toast when the open count rises while the tab is
// hidden. Clicking the bell opens a panel that fetches the full open list
// (/notifications), each row acknowledgeable in place or click-through to
// the tab that shows the underlying record.
//
// Click-to-open, click-outside / Escape to close — same interaction model
// as NavDropdown. Panel hangs from the right edge (align right) since the
// bell sits at the right end of the bar next to the user menu.
//
// Props:
//   onOpen(linkTab, linkEntity)  — called when a row is clicked; the parent
//     (Dashboard.js) switches activeTab to linkTab. linkEntity is passed
//     through for a future deep-open; v1 just switches the tab.

const SEVERITY_DOT = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-slate-400',
};

function timeAgo(stamp) {
  if (!stamp) return '';
  // Stored as "YYYY-MM-DD HH:MM:SS" (UTC) by D1's datetime('now').
  const then = new Date(stamp.replace(' ', 'T') + 'Z').getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function NotificationBell({ onOpen, onSeeAll }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(isSoundMuted());
  const containerRef = useRef(null);

  // Unlock the AudioContext on the first interaction anywhere in the app so
  // the first real alert can actually sound (browsers block audio started
  // before a user gesture). One-shot.
  useEffect(() => {
    const prime = () => {
      primeAudio();
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
    window.addEventListener('pointerdown', prime, { once: true });
    window.addEventListener('keydown', prime, { once: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  // Pause the count poll while the panel is open — the list fetch below
  // already has fresher data, and closing triggers an immediate refresh.
  const { count, refresh } = useNotifications({ active: !open, notify: true, sound: !muted });

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      setSoundMuted(next);
      // Play a confirmation tone when turning sound ON so the user hears
      // what an alert will sound like.
      if (!next) { primeAudio(); try { playAlert('info'); } catch { /* ignore */ } }
      return next;
    });
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/notifications?status=open');
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setItems(data.notifications || []);
    } catch {
      /* keep whatever we had */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadList();
    else refresh();
  }, [open, loadList, refresh]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    function handleKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const acknowledge = useCallback(async (id) => {
    // Optimistic — drop it from the list immediately.
    setItems((prev) => prev.filter((n) => n.id !== id));
    try {
      await apiFetch(`/notifications/${id}/acknowledge`, { method: 'POST' });
    } catch {
      /* if it fails the next poll/list re-adds it */
    }
  }, []);

  const handleRowClick = useCallback((n) => {
    setOpen(false);
    if (n.link_tab && typeof onOpen === 'function') onOpen(n.link_tab, n.link_entity);
  }, [onOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
        className="relative flex items-center justify-center w-9 h-9 rounded text-shell-text hover:bg-shell-dark"
      >
        <Bell className="w-5 h-5" aria-hidden="true" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-semibold rounded-full px-1 min-w-[16px] text-center leading-tight">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-lg bg-white text-gray-800 shadow-xl ring-1 ring-black/5 z-50">
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold">Notifications</span>
            <div className="flex items-center gap-2">
              {count > 0 && <span className="text-xs text-gray-700">{count} open</span>}
              <button
                type="button"
                onClick={toggleMuted}
                aria-label={muted ? 'Turn alert sound on' : 'Turn alert sound off'}
                title={muted ? 'Alert sound is off' : 'Alert sound is on'}
                className="text-gray-600 hover:text-gray-700"
              >
                {muted
                  ? <VolumeX className="w-4 h-4" aria-hidden="true" />
                  : <Volume2 className="w-4 h-4" aria-hidden="true" />}
              </button>
            </div>
          </div>

          {loading && items.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-700">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-700">Nothing needs your attention.</div>
          )}

          <ul className="divide-y divide-gray-100">
            {items.map((n) => (
              <li key={n.id} className="px-4 py-3 hover:bg-gray-50">
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_DOT[n.severity] || SEVERITY_DOT.info}`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => handleRowClick(n)}
                      className="block text-left w-full"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-gray-600 mt-0.5">{n.body}</p>}
                      <p className="text-xs text-gray-600 mt-1">{timeAgo(n.last_seen_at)}</p>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => acknowledge(n.id)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0"
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {onSeeAll && (
            <div className="px-4 py-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setOpen(false); onSeeAll(); }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                See all notifications & history →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
